import { useNavigate } from "react-router-dom";
import { useClient } from "@app/portal-sdk";
import { AppCard, AppIconTile, Box, Button, EmptyState, FolderOutlinedIcon, Stack, Typography } from "@app/ui-kit";
import { APP_REGISTRY } from "../apps/registry";
import { Stoplight } from "../components/Stoplight";

const paragraphs = (text: string) =>
  text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

/** The client's landing page: where we stand, the status note, then everything they can open. */
export const ClientHome = () => {
  const navigate = useNavigate();
  const { client } = useClient();
  const base = `/c/${client.slug}`;
  const documentsOn = client.features.documents !== false;
  const apps = client.enabledApps.map((slug) => APP_REGISTRY[slug]).filter(Boolean);
  const accent = client.brand.accent;
  // SHL-4: the stoplight appears only when there is something to say: a health, or a note. Neither, no control.
  const health = client.health ?? null;
  const hasStatus = Boolean(client.statusNote) || Boolean(health);

  return (
    <Stack spacing={5}>
      <Box sx={{ pb: 3, borderBottom: "1.5px solid", borderColor: accent ?? "divider" }} data-testid="client-banner">
        <Box>
          {client.logoUrl && (
            <Box component="img" src={client.logoUrl} alt={client.name} sx={{ height: 56, width: "auto", maxWidth: 240, display: "block", mb: 2.5 }} />
          )}
          <Typography variant="overline" sx={{ color: accent ?? "secondary.main", display: "block", mb: 1 }}>
            Client Portal
          </Typography>
          <Typography variant="h2" sx={{ color: "text.primary" }}>
            {client.name}
          </Typography>
          {client.brand.tagline && (
            <Typography variant="body1" sx={{ color: "text.secondary", mt: 1.5, maxWidth: 640 }}>
              {client.brand.tagline}
            </Typography>
          )}
        </Box>
      </Box>

      {/* SHL-4, placed per Blair's walkthrough: the stoplight sits to the right of the engagement status card, not in the banner, with a little room at the right. A note with no health shows the card alone; a health with no note shows the stoplight alone. */}
      {hasStatus && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, pr: { xs: 0, md: 2 }, flexWrap: "wrap" }} data-testid="status-row">
          {client.statusNote && (
            <Box id="engagement-status" data-testid="engagement-status" sx={{ flex: 1, minWidth: 280 }}>
              <AppCard eyebrow="Engagement status" featured>
                <Stack spacing={1.5}>
                  {paragraphs(client.statusNote).map((p, i) => (
                    <Typography key={i} variant="body1" sx={{ color: "rgba(255,255,255,0.86)" }}>
                      {p}
                    </Typography>
                  ))}
                </Stack>
              </AppCard>
            </Box>
          )}
          {health && <Box sx={{ px: 2, flexShrink: 0 }}><Stoplight health={health} /></Box>}
        </Box>
      )}

      {!documentsOn && apps.length === 0 ? (
        <EmptyState
          eyebrow="Getting Ready"
          title="Blair is preparing your workspace"
          description="Documents and applications will appear here as soon as they are shared with you."
        />
      ) : (
        /* SHL-2: rows stretch so cards beside each other share a height; a card alone in its row is the only thing that sets it. */
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, alignItems: "stretch" }} data-testid="card-grid">
          {documentsOn && (
            <AppCard
              eyebrow="Files"
              title="Documents"
              subtitle="Deliverables, briefings and working files BKDP has shared with you."
              icon={<AppIconTile icon={FolderOutlinedIcon} color={accent ?? undefined} />}
              action={
                <Button size="small" variant="outlined" onClick={() => navigate(`${base}/documents`)}>
                  Open
                </Button>
              }
            />
          )}
          {apps.map((def) => (
            <AppCard
              key={def.slug}
              eyebrow="Application"
              title={def.name}
              subtitle={def.description}
              icon={<AppIconTile icon={def.icon} color={def.iconColor} />}
              action={
                <Button size="small" variant="outlined" onClick={() => navigate(`${base}/apps/${def.slug}`)}>
                  Open
                </Button>
              }
            />
          ))}
        </Box>
      )}
    </Stack>
  );
};
